import { AlertTriangle, Clock, Coins, Network } from "lucide-react";

import type { EstimateOut } from "@/entities/projects/types";
import { cn } from "@/shared/lib/cn";
import { formatNumber } from "@/shared/lib/format";

export interface EstimatePanelProps {
  estimate: EstimateOut | null;
  loading?: boolean;
}

export function EstimatePanel({
  estimate,
  loading,
}: EstimatePanelProps): JSX.Element {
  if (!estimate) {
    return (
      <div className="glass-card p-5 text-sm text-muted-foreground">
        Подберите параметры — посчитаю стоимость и время.
      </div>
    );
  }

  const minutes =
    estimate.estimated_minutes_max === estimate.estimated_minutes_min
      ? `≈ ${estimate.estimated_minutes_min} мин`
      : `до ${estimate.estimated_minutes_max} мин`;

  return (
    <div
      className={cn(
        "glass-card relative overflow-hidden p-5 transition-opacity",
        loading && "opacity-60",
      )}
    >
      <div className="mb-3 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        Прогноз
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiInline
          icon={Network}
          label="Запросов Wordstat"
          value={formatNumber(estimate.total_calls)}
          accent="purple"
        />
        <KpiInline
          icon={Coins}
          label="Стоимость"
          value={`${estimate.cost_rub.toFixed(2)} ₽`}
          accent="cyan"
        />
        <KpiInline
          icon={Clock}
          label="Время сбора"
          value={minutes}
          accent="green"
        />
      </div>

      {estimate.will_hit_quota && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-pink/30 bg-pink/[0.08] p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-pink" />
          <div className="space-y-1">
            <div className="font-semibold text-white">
              Превысит часовую квоту 100 запросов/час
            </div>
            <div className="text-xs text-muted-foreground">
              Сбор займёт до {estimate.estimated_minutes_max} минут — клиент
              сам подождёт восстановления квоты.
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-3 gap-2 text-xs text-muted-foreground">
        <div>сидов: {formatNumber(estimate.seeds_count)}</div>
        <div>расширений: {formatNumber(estimate.expansion_calls)}</div>
        <div>динамики: {formatNumber(estimate.dynamics_calls)}</div>
      </div>
    </div>
  );
}

function KpiInline({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  accent: "purple" | "cyan" | "green";
}): JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <div
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white",
          accent === "purple" && "bg-gradient-to-br from-purple to-purple-dark shadow-glow-purple",
          accent === "cyan" && "bg-gradient-to-br from-cyan to-cyan-dark shadow-glow-cyan",
          accent === "green" && "bg-gradient-to-br from-green to-green-dark shadow-glow-green",
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="font-mono text-base font-bold text-white tabular-nums">
          {value}
        </div>
      </div>
    </div>
  );
}
